export default class FaqBlock {
	/**
	 * The breakpoint between desktop and mobile
	 */
	desktopBreakpoint;

	/**
	 * The block container element.
	 */
	faqBlockContainer;

	/**
	 * The currently chosen question.
	 */
	currentQuestion;

	/**
	 * The containers which hold the current question and answer.
	 */
	desktopAnswerContainers;

	/**
	 * constructor
	 *
	 * Initial setup
	 *
	 * @param  {HTMLElement} faqContainer The faq block container element
	 * @param  {number} breakpointWidth The breakpoint width for the accordion behaviour
	 */
	constructor( faqContainer, breakpointWidth = 992 ) {
		this.desktopBreakpoint = breakpointWidth;
		this.faqBlockContainer = faqContainer;
		this.desktopAnswerContainers = {
			answerTitle: faqContainer.querySelector( '.answer__title' ),
			answerContent: faqContainer.querySelector( '.answer__content' ),
		};

		// Populate the chosenQuestion with the first question, regardless of desktop or mobile
		const firstQuestion = faqContainer.querySelector( '.question-list__item:first-child' );

		if ( this.isMobile() || ! this.enableDesktop() ) {
			this.toggleQuestionOnMobile( firstQuestion );
		} else if ( this.enableDesktop() ) {
			this.toggleQuestionOnDesktop( firstQuestion );
		}

		this.currentQuestion = firstQuestion;
		this.addEventListenersToFaqs();
	}

	/**
	 * addEventListenersToFaqs
	 *
	 * Calls the `addEventListenersToFaq` on all faqs
	 *
	 */
	addEventListenersToFaqs() {
		const clickableQuestions = this.faqBlockContainer.querySelectorAll( '.question-list__item-title' );
		clickableQuestions.forEach( clickableQuestion => {
			this.addEventListenersToFaq( clickableQuestion );
		} );
	}

	/**
	 * addEventListenersToFaq
	 *
	 * @param  {HTMLElement} faqItem The element on which we want to add a event listener.
	 */
	addEventListenersToFaq( faqItem ) {
		[ 'click', 'keydown' ].forEach( event => {
			faqItem.addEventListener( event, e => {
				const clickedQuestion = e.currentTarget.parentElement;

				// only enable Desktop listeners if desktop layout is enabled
				if ( this.isMobile() || ! this.enableDesktop() ) {
					this.toggleQuestionOnMobile( clickedQuestion );
				} else if ( this.enableDesktop() ) {
					this.toggleQuestionOnDesktop( clickedQuestion );
				}
			} );
		} );
	}

	/**
	 * toggleQuestionOnMobile
	 *
	 * Handles the click on a question on mobile
	 *
	 * @param  {HTMLElement} chosenQuestion The clicked question
	 */
	toggleQuestionOnMobile( chosenQuestion ) {
		this.toggleSelectedQuestionClasses( chosenQuestion );

		this.fetchAndInsertMobileContent( chosenQuestion );

		// Update the currentQuestion to the clicked question if it's a new one or null if the user is closing the currently open one.
		this.currentQuestion = chosenQuestion === this.currentQuestion ? null : chosenQuestion;
	}

	/**
	 * toggleQuestionOnDesktop
	 *
	 * Handles the click on a question on desktop
	 *
	 * @param  {HTMLElement} chosenQuestion The clicked question
	 */
	toggleQuestionOnDesktop( chosenQuestion ) {
		// Bail if the same question is clicked multiple times on desktop.
		if ( chosenQuestion === this.currentQuestion ) {
			return;
		}

		this.toggleSelectedQuestionClasses( chosenQuestion );

		this.fetchAndInsertDesktopContent( chosenQuestion );

		this.currentQuestion = chosenQuestion;
	}

	/**
	 * fetchAndInsertMobileContent
	 *
	 * Replaces the content when a new question is clicked on desktop
	 *
	 * @param  {HTMLElement} chosenQuestion The clicked question
	 */
	fetchAndInsertMobileContent( chosenQuestion ) {
		// if no posts selected via manual selection or contextual posts not found, return to prevent a console error
		if ( ! chosenQuestion ) {
			return;
		}

		if ( this.currentQuestion ) {
			// Remove the content and class from the previously selected question
			const currentQuestionContentContainer = this.currentQuestion.querySelector( '.question-list__accordion-content' );

			currentQuestionContentContainer.setAttribute( 'aria-expanded', false );
		}

		if ( chosenQuestion !== this.currentQuestion ) {
			// Add the content and class to the chosen question
			const chosenQuestionContentContainer = chosenQuestion.querySelector( '.question-list__accordion-content' );

			chosenQuestionContentContainer.setAttribute( 'aria-expanded', true );
		}

		// do this in case user clicks question on mobile and see's a blank Q+A column when switching to landscape or a larger viewport size
		this.fetchAndInsertDesktopContent( chosenQuestion );
	}

	/**
	 * fetchAndInsertDesktopContent
	 *
	 * Replaces the content when a new question is clicked on desktop
	 *
	 * @param {HTMLElement} chosenQuestion The clicked question
	 */
	fetchAndInsertDesktopContent( chosenQuestion ) {
		const { answerTitle, answerContent } = this.desktopAnswerContainers;

		answerTitle.innerHTML = chosenQuestion.querySelector( '.question-list__item-title' ).textContent;

		// clear answerContent.innerHTML then getChosenQuestionAnswer
		answerContent.innerHTML = '';
		answerContent.innerHTML = this.getChosenQuestionAnswer( chosenQuestion );
	}

	/**
	 * toggleSelectedQuestionClasses
	 *
	 * Toggles the `question-list__item--current` on the clicked and/or previously active question
	 *
	 * @param {HTMLElement} chosenQuestion The clicked question
	 */
	toggleSelectedQuestionClasses( chosenQuestion ) {
		if ( this.currentQuestion ) {
			// Remove the content and class from the previously selected question
			this.currentQuestion.classList.remove( 'question-list__item--current' );
		}

		if ( ! chosenQuestion ) {
			return;
		}

		// Don't add the class back if it's the same accordion closing on mobile
		if ( ( chosenQuestion !== this.currentQuestion ) ) {
			chosenQuestion.classList.add( 'question-list__item--current' );
		}
	}
	/**
	 * getChosenQuestionAnswer
	 *
	 * Fetches, sanitizes and returns the answer of the chosen question
	 *
	 * @param  {HTMLElement} chosenQuestion The clicked question
	 */
	getChosenQuestionAnswer( chosenQuestion ) {
		return chosenQuestion.dataset?.answer ?? '';
	}

	/**
	 * isMobile
	 *
	 * Check wether the current viewport width is less than then `desktopBreakpoint`
	 *
	 * @return {boolean} If the current device is mobile-sized
	 */
	isMobile() {
		return this.desktopBreakpoint >= window.innerWidth;
	}

	/**
	 * enableDesktop
	 *
	 * Check if enableDesktop layout option is active
	 *
	 * @return {boolean} If the layout should have two column layout enabled
	 */
	enableDesktop() {
		if ( ! this.faqBlockContainer.classList.contains( 'enable-desktop' ) ) {
			return false;
		}

		return true;
	}
}
